// ---------------------------------------------------------------------------
// PostgREST caps every response at the project's "max rows" setting (1000 by
// default) and says nothing when it truncates -- you get a short array, not an
// error. A turf larger than the cap, or a season's worth of visits, would come
// back silently incomplete: canvassers would be sent to a subset of their turf
// and never know.
//
// Every list read goes through here. Deliberately does NOT stop on a short
// page, because the server's cap may be lower than PAGE_SIZE; it stops only on
// an empty one, at the cost of one extra round trip.
// ---------------------------------------------------------------------------

export const PAGE_SIZE = 1000;

export interface PageResult<T> {
  data: T[] | null;
  error: { message: string } | null;
}

export async function fetchAllPages<T>(
  page: (from: number, to: number) => PromiseLike<PageResult<T>>,
): Promise<T[]> {
  const rows: T[] = [];

  for (let from = 0; ; ) {
    const { data, error } = await page(from, from + PAGE_SIZE - 1);
    if (error) throw new Error(error.message);

    const batch = data ?? [];
    if (batch.length === 0) break;

    rows.push(...batch);
    from += batch.length;
  }

  return rows;
}

/** The last row of a page, which the next page starts strictly after. */
export interface Keyset {
  stamp: string;
  id: string;
}

/**
 * A PostgREST `or` filter for rows strictly after `after` in
 * `(column, id)` order. The stamp is quoted because it holds `:` and `.`,
 * which are reserved inside a logic tree.
 */
export function keysetAfter(column: string, after: Keyset): string {
  const stamp = `"${after.stamp}"`;
  return `${column}.gt.${stamp},and(${column}.eq.${stamp},id.gt.${after.id})`;
}

/**
 * Every row, paged by keyset instead of by offset.
 *
 * Offsets drift. Pages are separate queries, so a row updated between two of
 * them moves to the end and shifts everything after it back by one, and the
 * row on the boundary is never returned. Rows sharing a timestamp -- every
 * household one redraw touched -- may also come back in a different order on
 * each page. For the sync pull either is permanent: the skipped row sits
 * below the cursor, and no later pull asks for it again.
 *
 * `page` must order by `column` and then `id`, apply
 * `keysetAfter(column, after)` when `after` is given, and limit to PAGE_SIZE.
 * Stops on an empty page, for the same reason fetchAllPages does.
 */
export async function fetchAllAfter<T>(
  column: string,
  page: (after: Keyset | null) => PromiseLike<PageResult<T>>,
): Promise<T[]> {
  const rows: T[] = [];

  for (let after: Keyset | null = null; ; ) {
    const { data, error } = await page(after);
    if (error) throw new Error(error.message);

    const batch = data ?? [];
    if (batch.length === 0) break;

    rows.push(...batch);
    const last = batch[batch.length - 1] as Record<string, unknown>;
    after = { stamp: String(last[column]), id: String(last['id']) };
  }

  return rows;
}
